import * as React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import { CustomDataProvider } from '../provider/CustumDataProvider';
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  SelectField,
  DateInput,
  Filter,
  SelectInput,
  TopToolbar,
  ExportButton,
  BulkActionsToolbar,
  BulkDeleteButton,
  Pagination,
  useTranslate,
  useRecordContext,
  useListFilterContext,
  useListSortContext,
  useListContext,
  SearchInput,
  useResourceContext,
  useGetIdentity,
  useGetOne,
  TextInput,
  useInfiniteGetList,
  useRefresh,
  useNotify
} from 'react-admin';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// Comment out the missing imports and create placeholder alternatives below
// import InlineOpenCardModal from '../layout/admin/InlineOpenCardModal';
// import { WarehouseField } from './fields/WarehouseField';
import { Box, Button, Drawer, TextField as MaterialTextField, MenuItem, FormControl, Select, Typography, IconButton, CircularProgress, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { roles, getAllRoles, isWarehouseManagerRole, getWarehouseFromRole } from '../users/roles';
import axios from 'axios';
import ComponentToPrint, { CsvFile } from "./ComponentToPrint";

import ReactToPrint from "react-to-print";

import Empty from "../empty/Empty";







const OrderBulkActionButtons = (props) => {
  const [exportData, setExportData] = useState([]);
  const componentRef = useRef();
  const initialExportData = useMemo(() => [], []);

  const handleClick = () => {
    if (props.selectedIds.length > 0) {
      CustomDataProvider.getMany("api/orders", {
        ids: [props.selectedIds],
      }).then(function (response) {
        setExportData(response?.data);
      });
    }
  };

  useEffect(() => {
    handleClick();
  }, [props.selectedIds]);

  return (
    <React.Fragment>
      {/* Add unique keys to direct children of React.Fragment */}
      <div key="print-container" style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {/* ComponentToPrint is inside the div, so the div needs the key */}
        <ComponentToPrint ref={componentRef} printData={exportData} />
        <ReactToPrint
          key="react-to-print-trigger" // Key for ReactToPrint
          trigger={() => (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginRight: "20px" }}>
              <ArrowDownwardIcon />
              <Button
                style={{ cursor: props.selectedIds.length === 0 ? 'default' : 'pointer', border: "none", padding: "5px 10px", borderRadius: "5px" }}
                onClick={() => {
                  // Only reset and fetch if items are selected
                  if (props.selectedIds.length > 0) {
                    setExportData(initialExportData);
                    handleClick();
                  }
                }}
                disabled={props.selectedIds.length === 0} // Disable button if nothing selected
              >
                {/* Change text based on selection */}
                {props.selectedIds.length > 0 ? `Download Selected (${props.selectedIds.length})` : "Download"}
              </Button>
            </div>
          )}
          content={() => componentRef.current}
        />
        <CsvFile key="csv-file-export" orders={exportData} /> {/* Key for CsvFile */}
      </div>
    </React.Fragment>

  );
};

export const OrderList = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [warehouseChoices, setWarehouseChoices] = useState([]);

  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [debouncedStatus, setDebouncedStatus] = useState("");
  const [debouncedWarehouse, setDebouncedWarehouse] = useState("");
  const [debouncedStartDate, setDebouncedStartDate] = useState("");
  const [debouncedEndDate, setDebouncedEndDate] = useState("");

  const [filtersApplied, setFiltersApplied] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [ordersData, setOrdersData] = useState([]);
  const [correctedOrders, setCorrectedOrders] = useState([]);
  const [zipcodeMappingsInitialized, setZipcodeMappingsInitialized] = useState(false);
  const refresh = useRefresh();
  const notify = useNotify();
  const [clientSideWarehouseFilter, setClientSideWarehouseFilter] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [userWarehouse, setUserWarehouse] = useState(null);
  const { accessToken } = JSON.parse(localStorage.getItem("auth")) || { accessToken: null };

  // Determine if current user is admin
  const isAdmin = Array.isArray(userRole) ? userRole.includes(roles.admin) : userRole === roles.admin;

  // Initialize component when it loads - simplified approach without zipcode mappings
  useEffect(() => {
    console.log('Initializing OrderList component...');
    
    // No zipcode mapping initialization needed
    setZipcodeMappingsInitialized(true);
    
    // Still refresh the list to ensure we have the latest data
    refresh();
  }, [refresh]);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!accessToken) {
        console.log("No access token found - cannot fetch role");
        return;
      }
      
      try {
        const auth = JSON.parse(localStorage.getItem("auth")) || {};
        const id = auth.id;
        
        console.log("Auth data from localStorage:", auth);
        console.log("User ID from auth:", id);
        
        if (!id) {
          console.log("User ID not found in auth data");
          return;
        }
        
        console.log("Fetching user role from API...");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/users/${id}`,
          {
            headers: {
              authorization: `Bearer ${accessToken}`
            }
          }
        );
        
        console.log("Full API response:", response.data);
        
        const fetchedRole = response?.data?.roles;
        console.log("Extracted role from API:", fetchedRole);
        
        setUserRole(fetchedRole);
        
        let warehouseFilter = null;
        
        // Get all roles including dynamic ones
        const allRoles = getAllRoles();
        console.log("All available roles:", allRoles);
        
        if (Array.isArray(fetchedRole)) {
          // Check for admin role first
          if (fetchedRole.includes(roles.admin)) {
            warehouseFilter = null;
          } else {
            // Check for any warehouse manager role
            for (const role of fetchedRole) {
              if (isWarehouseManagerRole(role)) {
                // Get the warehouse name using the utility function
                warehouseFilter = getWarehouseFromRole(role);
                console.log(`Found warehouse manager role: ${role}, setting filter to: ${warehouseFilter}`);
                break;
              }
            }
          }
        } else if (typeof fetchedRole === 'string') {
          // Handle single role as string
          if (fetchedRole === roles.admin) {
            warehouseFilter = null;
          } else if (isWarehouseManagerRole(fetchedRole)) {
            warehouseFilter = getWarehouseFromRole(fetchedRole);
            console.log(`Found warehouse manager role: ${fetchedRole}, setting filter to: ${warehouseFilter}`);
          }
        }
        
        console.log("Setting warehouse filter based on role:", warehouseFilter);
        setUserWarehouse(warehouseFilter);
        
        if (warehouseFilter && !warehouse) {
          // Set warehouse state to just the name
          setWarehouse(warehouseFilter);
          // Set debouncedWarehouse to the name as well
          setDebouncedWarehouse(warehouseFilter);
          // clientSideWarehouseFilter is no longer needed here
          console.log(`Initialized warehouse filter for role ${userRole} to: ${warehouseFilter}`);
        }
        
      } catch (error) {
        console.error("Error fetching user role:", error);
        
        // Get roles from localStorage - note auth.roles (plural) not auth.role
        const auth = JSON.parse(localStorage.getItem("auth")) || {};
        const fallbackRoles = auth.roles || [];
        console.log("Using fallback roles from localStorage:", fallbackRoles);
        
        // Set the user role from fallback
        setUserRole(fallbackRoles);
        
        // Process warehouse filter based on fallback roles
        let warehouseFilter = null;
        
        if (Array.isArray(fallbackRoles)) {
          // Check for admin role first
          if (fallbackRoles.includes(roles.admin)) {
            warehouseFilter = null;
          } else {
            // Check for any warehouse manager role using the utility function
            for (const role of fallbackRoles) {
              if (role && isWarehouseManagerRole(role)) {
                warehouseFilter = getWarehouseFromRole(role);
                console.log(`Found warehouse manager role from localStorage: ${role}, setting filter to: ${warehouseFilter}`);
                break;
              }
            }
          }
        } else if (typeof fallbackRoles === 'string' && isWarehouseManagerRole(fallbackRoles)) {
          warehouseFilter = getWarehouseFromRole(fallbackRoles);
          console.log(`Found dynamic warehouse role from localStorage: ${fallbackRoles}, setting filter to: ${warehouseFilter}`);
        }
        
        // Set the warehouse filter
        if (warehouseFilter && !warehouse) {
          console.log("Setting warehouse filter from localStorage fallback:", warehouseFilter);
          const warehouseParam = `where%5Bwarehouse%5D[in]=${warehouseFilter}`;
          setWarehouse(warehouseParam);
          setClientSideWarehouseFilter(warehouseFilter);
          setDebouncedWarehouse(warehouseParam);
        }
      }
    };
    
    fetchUserRole();
  }, [accessToken]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const auth = JSON.parse(localStorage.getItem("auth")) || {};
        const { accessToken } = auth;
        const apiUrl = process.env.REACT_APP_BASE_API_URL;
        
        const response = await axios.get(
          `${apiUrl}/wms/api/warehouses`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        const warehouseData = response.data.data || [];
        
        const warehouseNames = warehouseData.map(warehouse => warehouse.name);
        
        if (!warehouseNames.includes("Others")) {
          warehouseNames.push("Others");
        }
        
        localStorage.setItem("availableWarehouses", JSON.stringify(warehouseNames));
        localStorage.setItem("warehousesLastFetched", new Date().toISOString());
        
        // Fix warehouseChoices to use just the warehouse name as id
        const formattedWarehouses = warehouseNames.map(name => ({
          id: name,
          name: name
        }));

        if (!formattedWarehouses.some(w => w.name === "Others")) {
          formattedWarehouses.push({ id: "Others", name: "Others" });
        }

        const isAdmin = Array.isArray(userRole) ? userRole.includes(roles.admin) : userRole === roles.admin;
        if (!isAdmin && userWarehouse) {
          formattedWarehouses = formattedWarehouses.filter(w => w.name === userWarehouse);
          console.log(`Filtered warehouse choices for ${userWarehouse} manager:`, formattedWarehouses);
          if (formattedWarehouses.length === 1) {
            setWarehouse(formattedWarehouses[0].id);
            setClientSideWarehouseFilter(formattedWarehouses[0].name);
          }
        }
        
        setWarehouseChoices(formattedWarehouses);
      } catch (error) {
        console.error('Error fetching warehouses:', error);
        setWarehouseChoices([]);
      }
    };
    
    fetchWarehouses();

    const refreshInterval = setInterval(fetchWarehouses, 5 * 60 * 1000); // Refresh every 5 minutes
    
    return () => {
      clearInterval(refreshInterval); // Clean up on component unmount
    };
  }, [userRole, userWarehouse]);

  const handleRowClick = (id) => {
    const editUrl = `/admin/api/orders/${id}`;
    window.open(editUrl, '_blank');
  };

  const applyAllFilters = () => {
    console.log("Applying filters:", { status, warehouse, startDate, endDate, searchText });
    
    const isAdmin = Array.isArray(userRole) ? userRole.includes(roles.admin) : userRole === roles.admin;
    let finalWarehouse = warehouse;
    
    if (!isAdmin && userWarehouse && (!warehouse || !warehouse.includes(userWarehouse))) {
      finalWarehouse = userWarehouse;
      console.log(`Enforcing warehouse filter for ${userWarehouse} manager:`, finalWarehouse);
      setWarehouse(finalWarehouse);
    } else {
      finalWarehouse = warehouse;
    }
    
    localStorage.setItem('selectedWarehouseFilter', finalWarehouse ? finalWarehouse : ''); // Keep track of selected filter for UI consistency if needed elsewhere

    localStorage.removeItem('ra.orderList.data');
    localStorage.removeItem('ra.orderList.params');

    // Directly set the debounced warehouse state with the final warehouse name (or empty string)
    // No need for client-side filtering logic here anymore
    setDebouncedWarehouse(finalWarehouse);
    // Remove clientSideWarehouseFilter state management
    // setClientSideWarehouseFilter(''); // Removed
    
    setDebouncedStatus(status);
    setDebouncedStartDate(startDate);
    setDebouncedEndDate(endDate);
    setDebouncedSearchText(searchText);
    setFiltersApplied(true);
  };

  const clearAllFilters = () => {
    setDebouncedSearchText('');
    setDebouncedStatus('');
    setDebouncedWarehouse('');
    setDebouncedStartDate('');
    setDebouncedEndDate('');
    setSearchText('');
    setStatus('');
    setWarehouse('');
    setEndDate('');
    setStartDate('');
    
    // Remove clientSideWarehouseFilter state management
    // setClientSideWarehouseFilter(''); // Removed
    localStorage.removeItem('selectedWarehouseFilter');
    localStorage.removeItem('ra.orderList.data');
    localStorage.removeItem('ra.orderList.params');
    
    // Explicitly construct a URL with empty filters to ensure clean state
    const params = new URLSearchParams();
    params.set('filter', JSON.stringify({})); // Empty filter object
    params.set('displayedFilters', JSON.stringify({}));
    params.set('order', 'ASC');
    params.set('page', '1');
    params.set('perPage', '25'); // Use the default perPage from useInfiniteGetList
    params.set('sort', 'processed_at'); // Use the default sort field from useInfiniteGetList
    navigate(`/admin/api/orders?${params.toString()}`, { replace: true }); // Use replace to avoid polluting history

    refresh();
  };

  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays <= 31;
  };

  const formatDate = (dateString, isEndDate = false) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    if (isEndDate) {
      return `${year}-${month}-${day}T23:59:59.999`;
    }
    return `${year}-${month}-${day}T00:00:00.000`;
  };

  const showAlert = (message) => {
    setDialogMessage(message);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const exportToCsv = () => {
    if (!debouncedStartDate || !debouncedEndDate) {
      showAlert("Please select both start and end dates for export.");
      return;
    }

    if (!validateDateRange(debouncedStartDate, debouncedEndDate)) {
      showAlert("Date range should not exceed 31 days.");
      return;
    }

    CustomDataProvider.getList('api/orders', {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: 'id', order: 'ASC' },
      filter: {
        q: debouncedSearchText,
        status: debouncedStatus,
        warehouse: debouncedWarehouse,
        startDate: debouncedStartDate ? formatDate(debouncedStartDate) : '',
        endDate: debouncedEndDate ? formatDate(debouncedEndDate, true) : '',
      }
    }).then(({ data }) => {
      const csvContent = [
        ['Order Number', 'Store (Warehouse)', 'Order Date', 'Subtotal', 'Coupon Discount', 'Shipping & Handling', 'Grand Total', 'Status', 'POS Bill Number', 'Customer Name', 'Customer Mobile', 'Tel:', 'Mail Id', 'Payment', 'Address 1', 'Address 2'].join(','),
        ...data.map(order => {
          const address2 = [
            order.shipping_address.address2,
            order.shipping_address.city,
            order.shipping_address.province,
            order.shipping_address.zip,
            order.shipping_address.country
          ].filter(Boolean).join(', ');

          return [
            order.order_number,
            order.warehouse,
            formatDate(order.processed_at),
            order.current_subtotal_price,
            order.discount,
            order.shipment,
            order.total_price,
            order.status,
            order.customer.posBillNumber,
            `${order.customer.first_name} ${order.customer.last_name}`,
            order.customer.phone || '',
            order.shipping_address.phone,
            order.customer.email,
            order.gateway,
            `"${order.shipping_address.address1}"`,
            `"${address2}"`
          ].join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'orders_export.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  const ListActions = () => {
    const isAdmin = Array.isArray(userRole) ? userRole.includes(roles.admin) : userRole === roles.admin;
    const isFilterApplied = filtersApplied && (debouncedSearchText || debouncedStatus || debouncedWarehouse || debouncedStartDate || debouncedEndDate);
    const isDateRangeValid = validateDateRange(debouncedStartDate, debouncedEndDate);
  
    const handleApplyFilters = () => {
      console.log("handleApplyFilters called", { startDate, endDate });
      
      if (startDate && endDate) {
        if (!validateDateRange(startDate, endDate)) {
          showAlert("Date range should not exceed 31 days.");
        } else {
          applyAllFilters();
        }
      } else if (startDate || endDate) {
        showAlert("Please select both start and end dates.");
      } else {
        applyAllFilters();
      }
    };
  
    return (
      <TopToolbar>
        {isAdmin && isFilterApplied && isDateRangeValid && (
          <Button 
            style={{ fontSize: '12px', marginRight: '10px', marginBottom: '10px' }} 
            onClick={exportToCsv}
            variant="contained"
          >
            Export  .CSV
          </Button>
        )}
        <div style={{display:'flex', gap:'10px', marginBottom:'10px'}}>
          <Button style={{fontSize:'12px'}} onClick={handleApplyFilters} variant="contained">Apply Filters</Button>
          <Button style={{fontSize:'12px'}} onClick={clearAllFilters} variant="contained">Clear Filters</Button>
        </div>
      </TopToolbar>
    );
  };

  const orderFilters = [
    <TextInput
      label="Search by shopify order number or name"
      source="q"
      alwaysOn
      style={{ width: "310px" }}
      onChange={(e) => setSearchText(e.target.value)}
      value={searchText}
    />,
    <SelectInput
      alwaysOn
      source="status"
      label="Status"
      choices={[
        { id: "where%5Bstatus%5D[equals]=pending", name: "Pending" },
        { id: "where%5Bstatus%5D[equals]=product_missing", name: "Product missing" },
        { id: "where%5Bstatus%5D[equals]=billed", name: "Billed" },
        { id: "where%5Bstatus%5D[equals]=dispatch", name: "Ready to Dispatch" },
        { id: "where%5Bstatus%5D[equals]=dispatched", name: "Dispatched" },
        { id: "where%5Bstatus%5D[equals]=delivered", name: "Delivered" },
        { id: "where%5Bstatus%5D[equals]=return", name: "Return" },
        { id: "where%5Bstatus%5D[equals]=cancelled", name: "Cancelled" },
        { id: "where%5Bstatus%5D[equals]=closed", name: "Closed" },
      ]}
      value={status}
      onChange={(e) => setStatus(e.target.value)}
    />,
    <SelectInput
      alwaysOn
      source="warehouse"
      label="Warehouse"
      choices={warehouseChoices}
      value={warehouse}
      onChange={(e) => setWarehouse(e.target.value)}
      disabled={!isAdmin}
    />,
    <DateInput
      onChange={(e) => setStartDate(e.target.value)}
      source="startDate"
      alwaysOn
      label="Start date"
      value={startDate}
      style={{ width: "140px" }}
    />,
    <DateInput
      onChange={(e) => setEndDate(e.target.value)}
      source="endDate"
      alwaysOn
      label="End Date"
      value={endDate}
      style={{ width: "140px" }}
    />,
  ];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get("q") || "";
    const status = searchParams.get("status") || "";
    const warehouse = searchParams.get("warehouse") || "";
    const startDate = searchParams.get("startDate") || "";
    const endDate = searchParams.get("endDate") || "";
    setDebouncedSearchText(q);
    setDebouncedStatus(status);
    setDebouncedWarehouse(warehouse);
    setDebouncedStartDate(startDate);
    setDebouncedEndDate(endDate);
  }, [location.search]);

  // Override useInfiniteGetList to handle warehouse assignment directly in OrderList
  const {
    data,
    total,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteGetList("api/orders", {
    pagination: { page: 1, perPage: 25 },
    filter: {
      q: debouncedSearchText,
      status: debouncedStatus,
      warehouse: debouncedWarehouse,
      startDate: debouncedStartDate ? formatDate(debouncedStartDate) : '',
      endDate: debouncedEndDate ? formatDate(debouncedEndDate, true) : '',
    },
    sort: { field: 'processed_at', order: 'DESC' },
  });

  useEffect(() => {
    if (data?.pages) {
      console.log('Processing fetched orders data...'); // Simplified log message
      
      // Removed dependency on clientSideWarehouseFilter
      const selectedWarehouse = localStorage.getItem('selectedWarehouseFilter') || '';
      
      const isAdmin = Array.isArray(userRole) ? userRole.includes(roles.admin) : userRole === roles.admin;
      const warehouseToFilter = !isAdmin && userWarehouse ? userWarehouse : selectedWarehouse;
      
      // Further processing logic based on warehouseToFilter can remain if needed,
      // but the complex client-side filtering logic based on clientSideWarehouseFilter state is removed.
      
    }
  }, [data, userRole, userWarehouse]); // Removed clientSideWarehouseFilter from dependency array

  const isOrderNumberSearch = debouncedSearchText && 
    !isNaN(debouncedSearchText) && 
    debouncedSearchText.length > 2;

  // Handle case when filters removed but no refresh occurred yet
  if (
    filtersApplied &&
    !debouncedSearchText &&
    !debouncedStatus &&
    !debouncedWarehouse &&
    !debouncedStartDate &&
    !debouncedEndDate
  ) {
    setFiltersApplied(false);
  }

  // Use ordersData directly rather than trying to manipulate it further
  const rawDisplayData = ordersData.length > 0
    ? ordersData
    : data?.pages.flatMap((page) => page.data) || [];

  // Filter out the specific order number '1234'
  const displayData = rawDisplayData.filter(order => order.order_number !== '1234' && order.order_number !== 'string');

// const displayData=rawDisplayData;
  
  // Apply client-side warehouse filtering if needed
  // const filteredDisplayData = clientSideWarehouseFilter 
  //   ? displayData.filter(order => order.warehouse === clientSideWarehouseFilter)
  //   : displayData;

  // // Map the display data to ensure warehouse information is consistent
  // const updatedDisplayData = filteredDisplayData.map(order => {
  //   // Get any additional info from correctedOrders if available
  //   const correctedOrder = correctedOrders.find(co => 
  //     co.id === order.id || 
  //     co.order_id === order.order_id || 
  //     co.order_number === order.order_number
  //   );
    
  //   // If we have a corrected version of this order with proper warehouse assignment, use it
  //   if (correctedOrder) {
  //     // For debugging manually transferred orders
  //     if (correctedOrder._manuallyTransferred) {
  //       console.log(`Display for manually transferred order ${order.order_number || order.id}: 
  //         Original warehouse = ${order.warehouse}
  //         Correctly transferred to = ${correctedOrder.warehouse}
  //         Transfer reason = ${correctedOrder._warehouseAssignmentReason || 'Manual transfer'}`);
  //     }
      
  //     // Use corrected data for display
  //     return {
  //       ...order,
  //       warehouse: correctedOrder.warehouse,
  //       warehouse_display: correctedOrder.warehouse,
  //       _manuallyTransferred: correctedOrder._manuallyTransferred,
  //       _warehouseCorrected: correctedOrder._warehouseCorrected,
  //       _warehouseAssignmentReason: correctedOrder._warehouseAssignmentReason
  //     };
  //   }
    
  //   // Fall back to the original order data
  //   return {
  //     ...order,
  //     warehouse_display: order.warehouse || "Others"
  //   };
  // });

  if (isLoading) {
    return (
      <p style={{ display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </p>
    );
  }
  if (error) {
    return <p>ERROR</p>;
  }

  return (
    <>
      <List
        dense
        filters={orderFilters}
        actions={<ListActions />}
        exporter={false}
        pagination={false}
      >
        <Datagrid
          data={displayData}
          empty={<Empty />}
          total={total}
          rowClick={(id, event) => {
            handleRowClick(id);
          }}
          bulkActionButtons={<OrderBulkActionButtons />}
        >
          <TextField source="order_number" label="Order Number" />
          <TextField source="warehouse" label="Store" />
          <TextField source="shipping_address.name" label="Ship-To-Name" />
          <DateField source="processed_at" label="Purchase date" />
          <TextField source="total_price" label="Grand Total ₹" />
          <TextField source="status" label="Status" />
          <TextField source="customer.posBillNumber" label="POS Bill Number" />
          <TextField source="customer.warehouseComment" label="Comment" />
        </Datagrid>
      </List>
      {hasNextPage && !isOrderNumberSearch && (
        <Button
          style={{ marginTop: "20px", marginBottom: "20px" }}
          variant="contained"
          disabled={isFetchingNextPage}
          onClick={() => fetchNextPage()}
        >
          Load More
        </Button>
      )}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Alert"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
